import { Injectable } from '@angular/core';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { IdName } from '@shared/models/id-name';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import { RxModel } from '@shared/models/rx-models/interfaces/rx-model';
import { RxConfiguration } from '@shared/models/rx-configuration';
import { ProcedureConfigurationService } from '@modules/order/services/procedure-configuration.service';
import { ProcedureFormStateService } from '@shared/services/procedure-form-state.service';
import { OrderQuery } from '@modules/order/state/order-query';
import { OrderStore } from '@modules/order/state/order-store';
import { TreatmentStageEnum } from '@modules/order/models/treatment-stage.enum';
import type { Maybe } from '@/types/globals';
import { enumEquals } from '@shared/utils/enum-utils';

@Injectable()
export class RxIntuitiveInvisalignDetailsFacade {
	procedureMap$ = this.orderQuery.procedureMap$;
	treatmentStage$ = this.orderQuery.treatmentStage$;
	currentAlignerId$ = this.orderQuery.currentAlignerId$;
	procedureTreatmentStages$ = this.shellQuery.procedureTreatmentStages$;
	types$ = this.procedureConfigurationService.types$;

	get storeState(): { treatmentStage: Maybe<IdName>; currentAlignerId: string | null } {
		return {
			treatmentStage: this.orderQuery.treatmentStage,
			currentAlignerId: this.orderQuery.currentAlignerId
		};
	}

	procedureTitle$: Observable<string> = this.procedureConfigurationService.procedures$.pipe(
		map(procedures => procedures.find(p => enumEquals(p.Id, ProcedureEnum.Invisalign))?.Name ?? ''),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	isReadOnly$: Observable<boolean> = this.shellQuery.getReadOnlyState('order').pipe(shareReplay({ bufferSize: 1, refCount: true }));

	treatmentStageState$ = this.procedureMap$.pipe(
		map(procedureMap => this.procedureFormStateService.getTreatmentStageState(procedureMap)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	currentAlignerIdState$ = combineLatest([this.procedureMap$, this.treatmentStage$]).pipe(
		map(([procedureMap, treatmentStage]) => this.procedureFormStateService.getCurrentAlignerIdState(procedureMap, treatmentStage)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	constructor(
		private readonly orderStore: OrderStore,
		private readonly orderQuery: OrderQuery,
		private readonly shellQuery: ShellQuery,
		private readonly procedureFormStateService: ProcedureFormStateService,
		private readonly procedureConfigurationService: ProcedureConfigurationService
	) {}

	handleTreatmentStageChanged(treatmentStage: IdName | null): void {
		const currentTreatmentStage = this.orderQuery.treatmentStage;
		const currentAlignerId = this.orderQuery.currentAlignerId;

		// If stage is unchanged but aligner needs initialization for non-InitialRecord, set it
		if (
			treatmentStage &&
			treatmentStage.Id === currentTreatmentStage?.Id &&
			treatmentStage.Id !== TreatmentStageEnum.InitialRecord &&
			!currentAlignerId
		) {
			// Stage already set but aligner is empty for non-InitialRecord → set to '0'
			this.orderStore.update({ currentAlignerId: '0' });

			return;
		}

		if (treatmentStage?.Id === currentTreatmentStage?.Id) {
			return;
		}

		let newState: { treatmentStage: IdName | null; currentAlignerId?: string | null } = {
			treatmentStage
		};

		if (!treatmentStage) {
			// Clear treatment stage → reset aligner
			newState = { ...newState, currentAlignerId: null };
		} else if (
			currentTreatmentStage?.Id === TreatmentStageEnum.InitialRecord &&
			treatmentStage.Id !== TreatmentStageEnum.InitialRecord
		) {
			// Exit from InitialRecord → set aligner to '0'
			newState = { ...newState, currentAlignerId: '0' };
		} else if (treatmentStage.Id === TreatmentStageEnum.InitialRecord) {
			// Enter InitialRecord → aligner = '0'
			newState = { ...newState, currentAlignerId: '0' };
		} else if (!currentTreatmentStage || !currentAlignerId) {
			// First selection of regular stage (from undefined/null) or empty aligner → set to '0'
			newState = { ...newState, currentAlignerId: '0' };
		}
		// Otherwise: switching between regular stages → aligner is preserved

		this.orderStore.update(newState);
	}

	handleCurrentAlignerChanged(currentAlignerId: string | null): void {
		this.orderStore.update({ currentAlignerId });
	}

	getLoadedRx(): Observable<{ treatmentStage: Maybe<IdName>; currentAlignerId: string | null }> {
		return combineLatest([this.shellQuery.rx$, this.shellQuery.isProcedureChanged$, this.shellQuery.rxConfiguration$]).pipe(
			filter((arr): arr is [RxModel, boolean, RxConfiguration] => !!arr[0] && !!arr[2] && !arr[1]),
			map(([rx, , rxConfiguration]) => {
				const procedureMap = rxConfiguration.RxRules.ProceduresMap.find(x => x.Id === rx.Order.ProcedureMapId);
				const isInvisalign = (procedureMap?.ProcedureId as ProcedureEnum) === ProcedureEnum.Invisalign;

				if (!isInvisalign) {
					return { treatmentStage: null, currentAlignerId: null };
				}

				const treatmentStage = rxConfiguration.TreatmentStages?.find(ts => ts.Id === rx.TreatmentStage) ?? null;

				return {
					treatmentStage,
					currentAlignerId: rx.AlignerNumber != null ? String(rx.AlignerNumber) : null
				};
			})
		);
	}
}
